import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { LifeBuoy, Send } from 'lucide-react';

const CATEGORIES = [
  { v: 'user_error', l: 'How do I do…' },
  { v: 'extension_issue', l: 'Guided Scan Extension issue' },
  { v: 'marketplace_issue', l: 'Marketplace / scan issue' },
  { v: 'platform_bug', l: 'Platform bug' },
  { v: 'account_issue', l: 'Account issue' },
  { v: 'payment_issue', l: 'Payment issue' },
  { v: 'subscription_issue', l: 'Subscription issue' },
  { v: 'feature_request', l: 'Feature request' },
  { v: 'other', l: 'Other' },
];

const Support = () => {
  const [items, setItems] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('other');
  const [severity, setSeverity] = useState('normal');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/support/tickets');
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your tickets');
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const ctx = {
        browser: navigator.userAgent,
        device: navigator.platform,
        os: navigator.userAgentData?.platform || navigator.platform,
      };
      await api.post('/support/tickets', {
        subject,
        body,
        category,
        severity,
        context: ctx,
      });
      toast.success('Ticket filed. Ari will bring the team\'s reply back to you here.');
      setSubject(''); setBody(''); setCategory('other'); setSeverity('normal');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not file ticket');
    } finally {
      setBusy(false);
    }
  };

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setSelected(data);
      setReply('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    try {
      await api.post(`/support/tickets/${selected.id}/reply`, { text: reply });
      setReply('');
      openTicket(selected.id);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-ari flex items-center justify-center text-white">
          <LifeBuoy size={18} />
        </div>
        <div>
          <h1 className="font-serif text-3xl text-zita-espresso">Support</h1>
          <p className="text-sm text-zita-mocha">Ari is the first line. If she can't help, she files a ticket to the Zita team — and delivers the reply here.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <form onSubmit={submit} className="zita-card p-5 space-y-3" data-testid="support-new-form">
          <div className="font-serif text-xl text-zita-espresso">New ticket</div>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            data-testid="support-subject"
            className="zita-input w-full px-3 py-2 text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="support-category" className="zita-input px-3 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="support-severity" className="zita-input px-3 py-2 text-sm">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <textarea
            required
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Describe what's happening. Include steps to reproduce if possible."
            data-testid="support-body"
            className="zita-input w-full px-3 py-2 text-sm"
          />
          <button type="submit" disabled={busy} data-testid="support-submit" className="zita-btn-primary w-full">
            {busy ? 'Filing…' : 'File ticket'}
          </button>
        </form>

        <div className="zita-card divide-y divide-zita-taupe/40" data-testid="support-list">
          <div className="p-4 font-serif text-xl text-zita-espresso">Your tickets</div>
          {!items.length && <div className="p-4 text-sm text-zita-mocha">No tickets yet.</div>}
          {items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => openTicket(t.id)}
              className={`w-full text-left px-4 py-3 hover:bg-zita-sand/40 ${selected?.id === t.id ? 'bg-zita-sand/60' : ''}`}
              data-testid={`support-row-${t.id}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium text-zita-espresso truncate">{t.subject}</div>
                <span className="text-[10px] uppercase tracking-wider text-zita-mocha">{t.status}</span>
              </div>
              <div className="text-xs text-zita-mocha mt-1">{t.severity} · {t.category?.replace('_', ' ')} · {new Date(t.created_at).toLocaleDateString()}</div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="zita-card p-5" data-testid="support-detail">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-2xl text-zita-espresso">{selected.subject}</h2>
            <div className="flex items-center gap-2">
              {['founder','super_admin'].includes(selected.takeover_by) && (
                <span
                  data-testid="support-detail-human-badge"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800"
                >
                  Human support
                </span>
              )}
              <span className="text-xs px-2 py-1 rounded-full bg-zita-sand text-zita-cocoa">{selected.status}</span>
            </div>
          </div>
          {['founder','super_admin'].includes(selected.takeover_by) && (
            <div className="mt-2 text-xs text-emerald-800">
              A real person from the Zita team is handling this conversation. Replies arrive here and in your email.
            </div>
          )}
          <div className="mt-4 space-y-3 max-h-80 overflow-y-auto pr-1">
            {(selected.messages || []).map((m, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg ${
                  ['founder','super_admin'].includes(m.author) ? 'bg-ari/10 ari-accent' :
                  m.author === 'ari' ? 'bg-zita-sand text-zita-espresso' :
                  'bg-zita-espresso text-zita-cream ml-6'
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1 flex items-center gap-2">
                  <span>
                    {['founder','super_admin'].includes(m.author) ? 'Zita Support' : m.author === 'ari' ? 'Ari' : 'You'} · {new Date(m.created_at).toLocaleString()}
                  </span>
                  {m.via_email && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-zita-sand text-zita-mocha">via email</span>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 items-end">
            <textarea
              rows={2}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Reply…"
              data-testid="support-detail-reply"
              className="zita-input flex-1 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={sendReply}
              disabled={!reply.trim()}
              data-testid="support-detail-send"
              className="zita-btn-primary inline-flex items-center gap-1"
            >
              <Send size={14} /> Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
